class TrGoogleReviews extends HTMLElement {
    requestTimeout = 8000;
    loadRequestId = 0;

    selectors = {
        slider: '[data-tr-js-google-reviews-slider]',
        slideList: '[data-tr-js-google-reviews-list]',
        status: '[data-tr-js-google-reviews-status]',
        showMoreText: '[data-tr-js-google-reviews-show-more]'
    };

    attributes = {
        apiUrl: 'data-tr-google-reviews-api-url',
        iconUrl: 'data-tr-google-reviews-icon-url',
        reviewsLimit: 'data-tr-google-reviews-limit',
        loadingLabel: 'data-tr-google-reviews-loading-label',
        errorLabel: 'data-tr-google-reviews-error-label',
        emptyLabel: 'data-tr-google-reviews-empty-label',
        readMoreLabel: 'data-tr-google-reviews-read-more-label',
        showLessLabel: 'data-tr-google-reviews-show-less-label'
    };

    stateClasses = {
        isVisible: 'is-visible'
    };

    connectedCallback() {
        this.cacheElements();
        this.bindEvents();

        // Sirf tab load karega jab user scroll karke section ke kareeb pahuchega
        if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting) {
                    this.observer.disconnect();
                    this.init();
                }
            }, { rootMargin: '200px' }); // 200px pehle hi trigger kar dega taake user ko wait na karna pare
            this.observer.observe(this);
        } else {
            this.init();
        }

        document.addEventListener('shopify:section:load', this.onSectionLoad);
    }

    disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        document.removeEventListener('shopify:section:load', this.onSectionLoad);
        this.destroy();
    }

    init() {
        this.isDestroyed = false;
        this.loadRequestId += 1;
        this.hideStatus();
        this.loadReviews();
    }

    bindEvents() {}

    onSectionLoad = event => {
        if (!this.sectionElement) return;
        if (this.sectionElement.id !== `shopify-section-${event.detail.sectionId}`)
            return;

        this.destroy();
        this.cacheElements();
        this.init();
    };

    async loadReviews() {
        const currentLoadRequestId = this.loadRequestId;
        const apiUrl = this.getAttribute(this.attributes.apiUrl);

        if (!apiUrl) {
            this.showStatus(this.getAttribute(this.attributes.emptyLabel));
            return;
        }

        this.showStatus(this.getAttribute(this.attributes.loadingLabel));

        try {
            const reviews = await this.getReviews(apiUrl);

            if (this.shouldIgnoreRequest(currentLoadRequestId)) return;

            const reviewsLimit =
                Number(this.getAttribute(this.attributes.reviewsLimit)) || 5;
            const visibleReviews = reviews.slice(0, reviewsLimit);

            if (!visibleReviews.length) {
                this.showStatus(this.getAttribute(this.attributes.emptyLabel));
                this.syncSlider();
                return;
            }

            this.renderReviews(visibleReviews);
            this.hideStatus();
            this.syncSlider();
        } catch (error) {
            if (this.shouldIgnoreRequest(currentLoadRequestId)) return;

            console.error('Unable to load Google reviews:', error);

            this.showStatus(this.getAttribute(this.attributes.errorLabel));
            this.syncSlider();
        }
    }

    getReviews(apiUrl) {
        return new Promise((resolve, reject) => {
            const timeoutId = window.setTimeout(() => {
                reject(new Error('Google reviews request timeout'));
            }, this.requestTimeout);

            fetch(apiUrl)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`Reviews API status: ${response.status}`);
                    }

                    return response.json();
                })
                .then(data => {
                    window.clearTimeout(timeoutId);

                    if (!data.success || !Array.isArray(data.reviews)) {
                        resolve([]);
                        return;
                    }

                    resolve(data.reviews);
                })
                .catch(error => {
                    window.clearTimeout(timeoutId);
                    reject(error);
                });
        });
    }

    renderReviews(reviews) {
        if (!this.slideListElement) return;

        const fragment = document.createDocumentFragment();

        reviews.forEach(review => {
            fragment.append(this.createReviewSlide(review));
        });

        this.slideListElement.innerHTML = '';
        this.slideListElement.append(fragment);

        this.showMoreTextElements = this.querySelectorAll(this.selectors.showMoreText);
    }

    createReviewSlide(review) {
        const slideElement = document.createElement('li');
        const cardElement = document.createElement('article');
        const authorGroupElement = document.createElement('header');
        const authorElement = document.createElement('h3');
        const metaElement = document.createElement('p');
        const ratingElement = document.createElement('p');
        const googleIconElement = document.createElement('img');
        const starsElement = document.createElement('span');
        const showMoreElement = document.createElement('tr-show-more-text');
        const quoteElement = document.createElement('blockquote');
        const textElement = document.createElement('div');
        const buttonElement = document.createElement('button');

        slideElement.className = 'tr-google-reviews__slide swiper-slide';
        cardElement.className =
            'tr-google-reviews__card tr-card-bg tr-card-bg--variant-gradient-green';
        authorGroupElement.className = 'tr-google-reviews__author-group';
        authorElement.className = 'tr-google-reviews__author';
        metaElement.className = 'tr-google-reviews__meta';
        ratingElement.className = 'tr-google-reviews__rating';
        googleIconElement.className = 'tr-google-reviews__google-icon';
        starsElement.className = 'tr-google-reviews__stars';
        showMoreElement.className = 'tr-google-reviews__show-more';
        showMoreElement.setAttribute('data-tr-js-google-reviews-show-more', '');
        quoteElement.className = 'tr-google-reviews__quote';
        textElement.className = 'tr-google-reviews__text tr-description';
        buttonElement.className = 'tr-google-reviews__read-more';

        authorElement.textContent = review.author || 'Google user';
        metaElement.textContent = review.date || 'Google review';
        ratingElement.setAttribute('aria-label', `${review.rating || 0} out of 5`);
        googleIconElement.src = this.getAttribute(this.attributes.iconUrl) || '';
        googleIconElement.width = 24;
        googleIconElement.height = 24;
        googleIconElement.alt = '';
        googleIconElement.loading = 'lazy';
        starsElement.textContent = this.getStars(review.rating);
        starsElement.setAttribute('aria-hidden', 'true');
        textElement.innerHTML = `<p>${this.formatReviewText(review.text || '')}</p>`;
        textElement.setAttribute('data-tr-js-show-more-text-content', '');
        buttonElement.type = 'button';
        buttonElement.textContent = this.getReadMoreLabel();
        buttonElement.hidden = true;
        buttonElement.setAttribute('data-tr-js-show-more-text-button', '');
        buttonElement.setAttribute('data-collapsed-text', this.getReadMoreLabel());
        buttonElement.setAttribute('data-expanded-text', this.getShowLessLabel());
        buttonElement.setAttribute('aria-expanded', 'false');

        quoteElement.append(textElement);
        showMoreElement.append(quoteElement, buttonElement);
        ratingElement.append(googleIconElement, starsElement);
        authorGroupElement.append(authorElement, metaElement);
        cardElement.append(authorGroupElement, ratingElement, showMoreElement);
        slideElement.append(cardElement);

        return slideElement;
    }

    getStars(rating) {
        const normalizedRating = Math.round(
            Math.max(0, Math.min(5, Number(rating) || 0))
        );

        return '\u2605'.repeat(normalizedRating);
    }

    formatReviewText(value) {
        return this.escapeHtml(value).replaceAll('\n', '<br>');
    }

    getReadMoreLabel() {
        return this.getAttribute(this.attributes.readMoreLabel) || 'Read more';
    }

    getShowLessLabel() {
        return (
            this.getAttribute(this.attributes.showLessLabel) ||
            this.getReadMoreLabel()
        );
    }

    escapeHtml(value) {
        return String(value)
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#39;');
    }

    showStatus(label) {
        if (!this.statusElement || !label) return;

        this.statusElement.textContent = label;
        this.statusElement.classList.add(this.stateClasses.isVisible);
    }

    hideStatus() {
        if (!this.statusElement) return;

        this.statusElement.textContent = '';
        this.statusElement.classList.remove(this.stateClasses.isVisible);
    }

    syncSlider() {
        if (!this.sliderElement || this.isDestroyed) return;

        if (
            this.sliderElement.slider &&
            typeof this.sliderElement.slider.update === 'function'
        ) {
            this.sliderElement.slider.update();
        } else if (typeof this.sliderElement.init === 'function') {
            this.sliderElement.init();
        }

        this.updateShowMoreText();
    }

    updateShowMoreText() {
        if (!this.showMoreTextElements) return;

        this.showMoreTextElements.forEach(showMoreTextElement => {
            if (typeof showMoreTextElement.update === 'function') {
                showMoreTextElement.update();
            }
        });
    }

    cacheElements() {
        this.sliderElement = this.querySelector(this.selectors.slider);
        this.slideListElement = this.querySelector(this.selectors.slideList);
        this.statusElement = this.querySelector(this.selectors.status);
        this.sectionElement = this.closest('.shopify-section');
    }

    shouldIgnoreRequest(loadRequestId) {
        return this.isDestroyed || loadRequestId !== this.loadRequestId;
    }

    destroy() {
        this.isDestroyed = true;
    }
}

if (!customElements.get('tr-google-reviews')) {
    customElements.define('tr-google-reviews', TrGoogleReviews);
}